import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import axios from 'axios';
import { setSession } from '../utils/jwt';
import { getUser } from '../services/authServices';
import { useNavigate } from 'react-router';
import { PATH_DASHBOARD, PATH_MANAGEMENT } from '../routes/paths';
import { snackActions } from 'src/components/notistack/SnackbarUtilsConfigurator';

// ----------------------------------------------------------------------
const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  courierPermissions: [],
  shopPermissions: [],
  nonShopPermissions: [],
  roles: [],
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, courierPermissions, shopPermissions, nonShopPermissions, roles } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      courierPermissions,
      shopPermissions,
      nonShopPermissions,
      roles,
    };
  },
  LOGIN: (state, action) => {
    const { user, courierPermissions, shopPermissions, nonShopPermissions, roles } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      courierPermissions,
      shopPermissions,
      nonShopPermissions,
      roles,
    };
  },
  UPDATE_PROFILE: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  GETUSER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  getuser: () => Promise.resolve(),
  register: () => Promise.resolve(),
  updateProfile: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        if (accessToken) {
          setSession(accessToken);
          const response = await getUser();
          if (response?.status === 200) {
            let shop_permission = new Set();
            for (const d of response?.data?.data?.roles) {
              if ('permissions' in d) {
                for (const perm of d['permissions']) {
                  if ('shops' in d) {
                    d.shops.map((shop) =>
                      shop_permission.add({
                        name: perm['name'],
                        shopId: shop?.id || null,
                      })
                    );
                  }
                  if ('shop' in d) {
                    shop_permission.add({
                      name: perm['name'],
                      shopId: d?.shop?.id || null,
                    });
                  }
                }
              }
            }
            shop_permission = Array.from(shop_permission);
            let non_shop_permission = new Set();
            for (const d of response?.data?.data?.roles) {
              if ('permissions' in d) {
                for (const perm of d['permissions']) {
                  if (!('shops' in d) && !('shop' in d) && !('couriers' in d)) {
                    non_shop_permission.add({
                      name: perm['name'],
                      shopId: null,
                    });
                  }
                }
              }
            }
            non_shop_permission = Array.from(non_shop_permission);
            let roles = new Set();
            for (const d of response?.data?.data?.roles) {
              roles.add(d['name']);
            }
            roles = Array.from(roles);
            let courier_permission = new Set();
            for (const d of response?.data?.data?.roles) {
              if ('permissions' in d) {
                for (const perm of d['permissions']) {
                  if ('couriers' in d) {
                    d.couriers.map((courier) =>
                      courier_permission.add({
                        name: perm['name'],
                        courierId: courier?.id || null,
                      })
                    );
                  }
                }
              }
            }
            courier_permission = Array.from(courier_permission);
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: true,
                user: response.data?.data,
                courierPermissions: courier_permission,
                shopPermissions: shop_permission,
                nonShopPermissions: non_shop_permission,
                roles: roles,
              },
            });
          } else {
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
          }
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (user) => {
    setSession(user.token);
    let shop_permission = new Set();
    for (const d of user?.user?.roles) {
      if ('permissions' in d) {
        for (const perm of d['permissions']) {
          if ('shops' in d) {
            d.shops.map((shop) =>
              shop_permission.add({
                name: perm['name'],
                shopId: shop?.id || null,
              })
            );
          }
          if ('shop' in d) {
            shop_permission.add({
              name: perm['name'],
              shopId: d.shop?.id || null,
            });
          }
        }
      }
    }
    shop_permission = Array.from(shop_permission);
    let non_shop_permission = new Set();
    for (const d of user?.user?.roles) {
      if ('permissions' in d) {
        for (const perm of d['permissions']) {
          if (!('shops' && 'couriers' && 'shop' in d)) {
            non_shop_permission.add({
              name: perm['name'],
              shopId: null,
            });
          }
        }
      }
    }
    non_shop_permission = Array.from(non_shop_permission);
    let roles = new Set();
    for (const d of user?.user?.roles) {
      roles.add(d['name']);
    }
    roles = Array.from(roles);

    const ALLOWED_ROLES = ['admin', 'supporter', 'notice supporter', 'shop owner', 'courier owner', 'cashier'];
    const hasAllowedRole = roles.some((role) => ALLOWED_ROLES.includes(role));

    if (!hasAllowedRole) {
      setSession(null);
      snackActions.error('شما دسترسی ورود به سیستم را ندارید. لطفاً با پشتیبانی تماس بگیرید.');

      setTimeout(() => {
        window.location.href = '/auth/login';
      }, 2000);
      throw new Error('Unauthorized');
      // navigate('/auth/login');
      // throw new Error('Unauthorized');
    }
    let courier_permission = new Set();
    for (const d of user?.user?.roles) {
      if ('permissions' in d) {
        for (const perm of d['permissions']) {
          if ('couriers' in d) {
            d.couriers.map((courier) =>
              courier_permission.add({
                name: perm['name'],
                courierId: courier?.id || null,
              })
            );
          }
        }
      }
    }
    courier_permission = Array.from(courier_permission);

    dispatch({
      type: 'LOGIN',
      payload: {
        user: user?.user,
        courierPermissions: courier_permission,
        shopPermissions: shop_permission,
        nonShopPermissions: non_shop_permission,
        roles: roles,
      },
    });
    if (shop_permission.length > 0) {
      const Id = shop_permission.map((item) => item.shopId)[0];
      navigate(PATH_MANAGEMENT.management.editShop.shopInvioce(Id));
    } else if (courier_permission.length > 0) {
      const Id = courier_permission.map((item) => item.courierId)[0];

      navigate(PATH_MANAGEMENT.management.courier.editCourier.courierInvioce(Id));
    } else {
      navigate(PATH_DASHBOARD.root);
    }
  };

  const updateProfile = async (values) => {
    dispatch({
      type: 'UPDATE_PROFILE',
      payload: {
        user: {
          ...state.user,
          profile: values,
        },
      },
    });
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    window.localStorage.setItem('accessToken', accessToken);
    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
